import { BandChart, Button } from '@toggle/design-system';
import { MappedEntity, SnakeMeta } from '@toggle/toggle';
import React, { FC, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Indicator } from '~/api/timeseries/timeseries-schema';
import { LivePriceView } from '~/components/live-price-view/LivePriceView';
import { useChartNavigate } from '~/hooks/use-chart-navigate/useChartNavigate';
import { useLivePrice } from '~/hooks/use-live-price';
import { globalPriceFormatter } from '~/utils/asset-price/assetPrice';
import { ChartSearchParamsAssetValue } from '~/utils/chart-search-utils/chart-search-utils';
import { mapIndicatorToSnake } from '~/views/indicators-widget/indicator-constants';

import { SpxTsData } from '../../hooks/useSpyArca/useSpyArca';
import { SingleWidget } from '../single-widget/SingleWidget';
import { LivePriceLine } from '../snake-chart/components/LivePriceLine';
import * as S from './ChartSpy.styles';

interface ChartSpyProps {
  entity: MappedEntity;
  priceSuffix: string;
  title?: string;
  tsData?: SpxTsData[];
  snakeMeta?: SnakeMeta;
  activeIndicator: Indicator;
}

export const ChartSpy: FC<ChartSpyProps> = ({
  entity,
  priceSuffix,
  title,
  tsData = [],
  snakeMeta,
  activeIndicator,
}) => {
  const { t } = useTranslation(['indicator', 'common']);
  const navigate = useChartNavigate();
  const uniqueId = useId();

  const priceEntities = snakeMeta?.last_value
    ? [
        {
          entity,
          lastKnownPrice: snakeMeta.last_value,
          priceBefore: snakeMeta.before_last_value,
        },
      ]
    : [];
  const livePriceState = useLivePrice(priceEntities);

  const navigateToSpyChart = () => {
    const spyValue: ChartSearchParamsAssetValue = [
      'spy_arca',
      'spy_arca.price.ds',
      0,
    ];
    const indicatorValue: ChartSearchParamsAssetValue = [
      'spy_arca',
      mapIndicatorToSnake[activeIndicator],
      1,
    ];

    navigate([spyValue, indicatorValue]);
  };

  const HeaderElement = (
    <>
      <S.FlexWrapper>
        <div>
          <S.Title>{title}</S.Title>
          <S.LatestPrice data-testid="widget-latest-price">
            {entity && (
              <LivePriceView
                livePriceInfo={livePriceState[entity.ticker]}
                formatPrice={p =>
                  globalPriceFormatter({
                    entity,
                    price: p,
                    priceSuffix,
                  })
                }
                lastTimestamp={snakeMeta?.last_timestamp}
              />
            )}
          </S.LatestPrice>
        </div>
        <S.ButtonContainer>
          <Button
            data-testid="btn-navigate-to-spy"
            variant="empty"
            size="small"
            iconName="Expand"
            onClick={navigateToSpyChart}
          />
        </S.ButtonContainer>
      </S.FlexWrapper>
      <S.Legend>
        <S.LegendItem>
          <S.BullishMarker />
          <S.Label>{t('indicator:bullishBias')}</S.Label>
        </S.LegendItem>
        <S.LegendItem>
          <S.BearishMarker />
          <S.Label>{t('indicator:bearishBias')}</S.Label>
        </S.LegendItem>
      </S.Legend>
    </>
  );

  const livePriceData = livePriceState[entity.ticker];

  return (
    <SingleWidget
      HeaderElement={HeaderElement}
      ContentElement={
        <S.ChartWrapper>
          <BandChart
            tsData={tsData}
            showLine
            renderAfterPlotNode={(xScale, yScale) => (
              <>
                {livePriceData?.isLive && (
                  <g clipPath={`url(#clip-${uniqueId})`}>
                    <LivePriceLine
                      x1={xScale(tsData.length - 1)}
                      x2={xScale(tsData.length)}
                      y1={yScale(livePriceData.lastKnownPrice)}
                      y2={yScale(livePriceData.price)}
                    />
                  </g>
                )}
              </>
            )}
          />
        </S.ChartWrapper>
      }
    />
  );
};
